// Public-holiday helper with lazy-loaded date-holidays.
//
// date-holidays bundles YAML for ~200 countries (~1 MB raw, ~280 KB gzipped).
// We lazy-import it in its own chunk so it doesn't block initial paint.
// First call returns []; once the library is loaded, subscribers are notified
// so React components can re-render with the data.

let HolidaysCtor = null;
let loadPromise = null;
const subscribers = new Set();
const cache = new Map();

function loadCtor() {
  if (HolidaysCtor) return Promise.resolve(HolidaysCtor);
  if (!loadPromise) {
    loadPromise = import("date-holidays").then(mod => {
      HolidaysCtor = mod.default || mod;
      subscribers.forEach(cb => { try { cb(); } catch { /* ignore */ } });
      return HolidaysCtor;
    }).catch(() => null);
  }
  return loadPromise;
}

// Kick off the import without waiting. Call this once after first paint.
export function preloadHolidays() { loadCtor(); }

// Subscribe to a one-time notification when the library finishes loading.
// Returns an unsubscribe function.
export function subscribeHolidaysReady(cb) {
  if (HolidaysCtor) { cb(); return () => {}; }
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

export function getHolidaysForYear(country, year) {
  const cc = (country || "HK").toUpperCase();
  const key = cc + ":" + year;
  if (cache.has(key)) return cache.get(key);
  if (!HolidaysCtor) {
    // Trigger lazy load; return empty array for this render. A subscriber will
    // force re-render once the library is ready.
    loadCtor();
    return [];
  }
  try {
    const hd = new HolidaysCtor(cc);
    const list = (hd.getHolidays(year) || [])
      .filter(h => h.type === "public" || h.type === "bank")
      .map(h => ({ date: String(h.date).slice(0, 10), name: h.name, type: h.type }));
    cache.set(key, list);
    return list;
  } catch {
    cache.set(key, []);
    return [];
  }
}

export function getHolidayForDate(country, isoDate) {
  if (!isoDate) return null;
  const year = parseInt(isoDate.slice(0, 4), 10);
  if (!year) return null;
  return getHolidaysForYear(country, year).find(h => h.date === isoDate) || null;
}

export const SUPPORTED_COUNTRIES = [
  { code: "HK", name: "香港 Hong Kong" },
  { code: "TW", name: "台灣 Taiwan" },
  { code: "CN", name: "中國 China" },
  { code: "MO", name: "澳門 Macau" },
  { code: "SG", name: "新加坡 Singapore" },
  { code: "MY", name: "馬來西亞 Malaysia" },
  { code: "JP", name: "日本 Japan" },
  { code: "KR", name: "韓國 Korea" },
  { code: "TH", name: "泰國 Thailand" },
  { code: "PH", name: "菲律賓 Philippines" },
  { code: "ID", name: "印尼 Indonesia" },
  { code: "VN", name: "越南 Vietnam" },
  { code: "AU", name: "Australia" },
  { code: "NZ", name: "New Zealand" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
];
